import { useEffect } from 'react';

export const getCurrentPath = () =>
  window.location.pathname + window.location.search + window.location.hash;

/**
 * Hook that tracks URL changes and calls the provided callback.
 *
 * We need to intercept history.pushState/replaceState because React Router v3
 * doesn't fire events when navigating programmatically. The popstate event only
 * fires on browser back/forward. This override is scoped to this component's
 * lifecycle and restored on unmount.
 */
export const useUrlTracking = (
  onUrlChange: (url: string) => void,
  getUrl: () => string = getCurrentPath
) => {
  useEffect(() => {
    const handleLocationChange = () => {
      onUrlChange(getUrl());
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function pushState(...args) {
      originalPushState.apply(this, args);
      handleLocationChange();
    };

    window.history.replaceState = function replaceState(...args) {
      originalReplaceState.apply(this, args);
      handleLocationChange();
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [onUrlChange, getUrl]);
};
